/**
 * Per-page access guard for analytics sub-pages. Admins see everything; viewers
 * see a page only if it's granted (deny-by-default). Admin-area pages require
 * analytics-admin. Hub-gated (v1) — the analytics API still enforces the base
 * `analytics` claim, so this is a UX/segmentation layer, not the data boundary.
 */
import type { ReactNode } from 'react';
import { isAnalyticsAdmin, canViewAnalyticsPage } from '@daaily/shared';
import { useProfile } from '../profile/ProfileProvider';

export function AnalyticsPageGuard({ entryKey, admin, children }: { entryKey: string; admin?: boolean; children: ReactNode }) {
  const { state } = useProfile();
  if (state.status !== 'ready') return null;
  const isAdmin = isAnalyticsAdmin({ isAppAdmin: state.isAdmin, role: state.user.tools?.analytics?.role });
  const allowed = admin ? isAdmin : canViewAnalyticsPage(isAdmin, state.user.analyticsPages ?? [], entryKey);
  if (allowed) return <>{children}</>;
  return (
    <main className="w-full max-w-form px-4 md:px-8 pt-6 pb-10">
      <div className="rounded-lg border border-line bg-surface px-6 py-10 text-center">
        <div className="text-sm font-medium text-fg">This dashboard isn't provisioned for you</div>
        <p className="text-sm text-fg-muted mt-1.5">
          Ask an Analytics admin to grant you access to this page.
        </p>
      </div>
    </main>
  );
}
